import { useState } from 'react';
import { useNavigate } from 'react-router';

import { usePortfolioSearchQuery } from '@entities/portfolio';
import { appPath } from '@shared/config';
import { useDebouncedValue } from '@shared/lib/debounce';
import type { GlobalSearchPanelProps } from '@widgets/global-search';

import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_LENGTH } from './layout.constant';

/** GlobalHeader 의 검색 슬롯을 채우는 쪽에서 쓰는 상태 */
export interface AppSearch {
  text: string;
  setText(value: string): void;
  setFocused(focused: boolean): void;
  /** 검색창에 초점이 있으면 패널 props, 아니면 null */
  panel: Omit<GlobalSearchPanelProps, 'id'> | null;
}

/** "검색어/회사명" → 검색어 · 회사명(서버에는 keyword · within 으로 따로 보낸다) */
function splitKeyword(keyword: string) {
  const [rawText = '', rawCompany = ''] = keyword.split('/');
  return { searchText: rawText.trim(), company: rawCompany.trim() };
}

/**
 * 헤더 검색. 입력이 멈추면(SEARCH_DEBOUNCE_MS) 검색어가 2글자 이상일 때 `GET /skimmer/portfolio`(결과는 포트폴리오별 묶음).
 * 입력 중 · 요청 중에는 "검색 중...". 결과를 누르면 그 포트폴리오 상세로 가고 검색어를 비운다.
 * 입력값은 이 hook 의 지역 상태다(헤더 밖에서 쓰지 않는다 — 전역 store 로 두지 않는다).
 */
export function useAppSearch(): AppSearch {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const keyword = useDebouncedValue(text.trim(), SEARCH_DEBOUNCE_MS);
  const { searchText, company } = splitKeyword(keyword);
  const guide = splitKeyword(text).searchText.length < SEARCH_MIN_LENGTH;
  const query = usePortfolioSearchQuery(
    { keyword: searchText, ...(company ? { within: company } : {}) },
    { enabled: focused && searchText.length >= SEARCH_MIN_LENGTH },
  );

  const typing = text.trim() !== keyword;
  const items = query.data ?? [];
  const status: GlobalSearchPanelProps['status'] =
    typing || query.isFetching || query.isPending
      ? 'loading'
      : items.length > 0
        ? 'default'
        : 'empty';

  return {
    text,
    setText,
    setFocused,
    panel: focused
      ? {
          guide,
          status,
          scope: company ? 'company' : 'all',
          searchText,
          searchCompany: company || undefined,
          items,
          onSelect: (item) => {
            setText('');
            setFocused(false);
            if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
            void navigate(appPath.portfolioDetail(item.idx));
          },
        }
      : null,
  };
}
